import React, { useState, useEffect } from 'react';
import '../App.css';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { PowerSettingsNew } from '@mui/icons-material';
import TemporaryDrawer from './TemporaryDrawer';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../store/auth-slice';
import { fetchCurrentCompanyDetails, setStoredCompanyDetails, getStoredCompanyDetails } from '../services/companyService';
import payrollHRComplianceLogo from '../images/PayrollHRComplianceLogo.png';
import Notification from './Notification';


function Navbar() {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [logoDimensions, setLogoDimensions] = useState({ width: 0, height: 0 });
  const [logoAspectRatio, setLogoAspectRatio] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isLogin = useSelector((state) => state.auth.isLogin);
  const activeUserName = useSelector((state) => state.auth.activeUserName);
  const companyLogoURL = useSelector((state) => state.auth.activeCompanyLogoURL);
  
  // Fallback values to ensure username and logo never disappear
  const displayUserName = activeUserName || localStorage.getItem('activeUserName') || '';
  const displayLogoURL = companyLogoURL || localStorage.getItem('companyLogoBase64') || '';
  
  // Check if current route is a login-related page
  const isLoginPage = () => {
    const currentPath = location.pathname;
    return currentPath === '/' || 
           currentPath === '/create-account' || 
           currentPath === '/forgot-password' || 
           currentPath === '/enter-otp' || 
           currentPath === '/new-password';
  };

  // Load login info from localStorage on mount
  useEffect(() => {
    const userName = localStorage.getItem('activeUserName');
    const logoUrl = localStorage.getItem('companyLogoURL');

    // Don't automatically set login state from localStorage
    // Only set login state after backend verification
  }, [dispatch]);

  useEffect(() => {
    // Define the async function inside the effect
    const getCompanyLogoURL = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/get-logo`, { credentials: 'include' });
        if (!response.ok) {
          // If no logo exists, don't clear existing logo - just return
          console.log('No company logo found on server');
          return;
        }
        const imageBlob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result;
          dispatch(authActions.setActiveCompanyLogoURL(base64data));
          localStorage.setItem('companyLogoBase64', base64data);
          localStorage.setItem('companyLogoURL', base64data);
        };
        reader.readAsDataURL(imageBlob);
      } catch (error) {
        console.error("Error fetching company logo:", error);
        // Don't clear existing logo on error - preserve current state
      }
    };

    // Always preserve username from localStorage
    const storedUserName = localStorage.getItem('activeUserName');
    if (storedUserName && !activeUserName) {
      dispatch(authActions.setActiveUserName(storedUserName));
    }

    const storedLogo = localStorage.getItem('companyLogoBase64');
    if (storedLogo) {
      dispatch(authActions.setActiveCompanyLogoURL(storedLogo));
    } else {
      getCompanyLogoURL();
    }
    const storedCompany = getStoredCompanyDetails();
    if (storedCompany) {
      dispatch(authActions.setActiveCompanyDetails(storedCompany));
    }
  }, [dispatch, activeUserName]);

  // Fetch login status from backend and update both Redux and localStorage
  useEffect(() => {
    const fetchLoginStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/is-loggedin`, {
          credentials: 'include',
        });
        if (response.status === 200) {
          const data = await response.json();
          dispatch(authActions.setLogin());
          dispatch(authActions.setActiveUserName(data.activeUserName));
          localStorage.setItem('activeUserName', data.activeUserName);
          if (data.companyLogoURL) {
            dispatch(authActions.setActiveCompanyLogoURL(data.companyLogoURL));
            localStorage.setItem('companyLogoURL', data.companyLogoURL);
            localStorage.setItem('companyLogoBase64', data.companyLogoURL);
          }
          const logoRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/get-logo`, { credentials: 'include' });
          if (logoRes.ok) {
            const imageBlob = await logoRes.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64data = reader.result;
              dispatch(authActions.setActiveCompanyLogoURL(base64data));
              localStorage.setItem('companyLogoBase64', base64data);
              localStorage.setItem('companyLogoURL', base64data);
            };
            reader.readAsDataURL(imageBlob);
          }
        } else {
          // Session is no longer active. Clear user data.
          dispatch(authActions.setLogout());
          dispatch(authActions.clearCompanyLogo());
          localStorage.removeItem('activeUserName');
          localStorage.removeItem('companyLogoURL');
          localStorage.removeItem('companyLogoBase64');
          localStorage.removeItem('activeCompanyDetails');
        }
        setIsInitialized(true);
      } catch (error) {
        // API call failed. Check if we have stored user data as fallback
        const storedUserName = localStorage.getItem('activeUserName');
        const storedLogo = localStorage.getItem('companyLogoBase64');
        
        if (storedUserName) {
          // If we have stored data, assume user is still logged in
          dispatch(authActions.setLogin());
          dispatch(authActions.setActiveUserName(storedUserName));
          if (storedLogo) {
            dispatch(authActions.setActiveCompanyLogoURL(storedLogo));
          }
          const storedCompany = getStoredCompanyDetails();
          if (storedCompany) {
            dispatch(authActions.setActiveCompanyDetails(storedCompany));
          }
        } else {
          // No stored data, clear everything
          dispatch(authActions.setLogout());
          dispatch(authActions.clearCompanyLogo());
          localStorage.removeItem('activeUserName');
          localStorage.removeItem('companyLogoURL');
          localStorage.removeItem('companyLogoBase64');
          localStorage.removeItem('activeCompanyDetails');
        }
        console.error('Error fetching login status:', error);
        setIsInitialized(true);
      }
    };

    fetchLoginStatus();
    const intervalId = setInterval(fetchLoginStatus, 5 * 60 * 1000); // every 5 minutes

    return () => clearInterval(intervalId);
  }, [dispatch]);

  const handleLogoutClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmLogout = async () => {
    handleCloseConfirmDialog();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/logout`, {
        credentials: 'include',
      });
      if (response.status === 200) {
        dispatch(authActions.setLogout());
        dispatch(authActions.clearCompanyLogo());
        localStorage.removeItem('activeUserName');
        localStorage.removeItem('companyLogoURL');
        localStorage.removeItem('companyLogoBase64');
        setNotify({ isOpen: true, message: 'Successfully Logged Out!', type: 'success' });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({ isOpen: true, message: 'Failed to logout. Please try again.', type: 'error' });
      }
    } catch (err) {
      setNotify({ isOpen: true, message: 'Logout error. Please try again.', type: 'error' });
    }
  };
  
  // New handler for the logo click
  const handleLogoClick = () => {
    // Only navigate if the user is logged in, assuming the dashboard is protected
    if (isLogin) {
      navigate('/dashboard');
    }
  };

  // Handler for company logo click - navigate to insert company logo page
  const handleCompanyLogoClick = () => {
    if (isLogin) {
      navigate('/company-master/insert-header');
    }
  };

  // Reset logo dimensions when logo URL changes
  useEffect(() => {
    if (displayLogoURL) {
      setLogoDimensions({ width: 0, height: 0 });
      setLogoAspectRatio(null);
    }
  }, [displayLogoURL]);

  // Handler for image load to get natural dimensions
  const handleLogoLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    const aspectRatio = naturalWidth / naturalHeight;
    setLogoDimensions({ width: naturalWidth, height: naturalHeight });
    setLogoAspectRatio(aspectRatio);
  };

  // Dynamic style calculation based on aspect ratio
  // This ensures the logo looks perfect regardless of shape (circular, square, rectangle)
  // Container uses fixed width to prevent layout shifts for username/logout button
  const getLogoContainerStyle = () => {
    // Container with fixed width to prevent layout shifts
    const containerStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '0px',
      marginTop: '5px',
      // Fixed width to prevent layout shifts - keeps username/logout button in place
      width: '120px',
      maxHeight: '70px',
      minHeight: '40px',
    };

    if (logoAspectRatio !== null) {
      // Determine if image is landscape, square, or portrait
      if (logoAspectRatio > 1.2) {
        // Wide/landscape image - constrain by height
        containerStyle.height = `${120 / logoAspectRatio}px`;
      } else if (logoAspectRatio < 0.8) {
        // Tall/portrait image - constrain by height
        containerStyle.height = '70px';
      } else {
        // Square or near-square image - use equal constraints
        const size = Math.min(70, 120 / logoAspectRatio);
        containerStyle.height = `${size}px`;
      }
    } else {
      // Default fallback - square container
      containerStyle.height = '70px';
    }

    return containerStyle;
  };

  // Image style that ensures no stretching or compression
  const getLogoImageStyle = () => {
    return {
      maxWidth: '100%',
      maxHeight: '100%',
      width: 'auto',
      height: 'auto',
      objectFit: 'contain', // Prevents stretching/compression
      objectPosition: 'center', // Centers the image
    };
  };

  return (

    <Box sx={{ flexGrow: 1 }}>

      <AppBar position="static" sx={{ backgroundColor: 'white' }}>

        <Toolbar>

          {isInitialized && isLogin && <TemporaryDrawer />}

          {/* Start of the modification */}
          <Box
            onClick={isLogin ? handleLogoClick : undefined} // Only allow click if logged in
            sx={{
              cursor: isLogin ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <img
              id="paytonic-logo"
              src={payrollHRComplianceLogo}
              alt="Payroll & HR Compliance"
              style={{
                height: '88px',
                width: 'auto',
                maxWidth: '520px',
                objectFit: 'contain',
                imageRendering: 'auto',
                display: 'block',
              }}
            />
          </Box>
          {/* End of the modification */}

          <Box sx={{ flexGrow: 1 }} />



          {/* Show username and logo when user is logged in AND not on login pages */}
          {!isLoginPage() && ((isInitialized && isLogin) || displayUserName) && (

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '16px' }}>
              {/* Logo above - clickable */}
              {displayLogoURL ? (
                <Box
                  onClick={handleCompanyLogoClick}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  <div style={getLogoContainerStyle()}>
                    <img
                      src={displayLogoURL}
                      alt="company logo"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      onLoad={handleLogoLoad}
                      style={getLogoImageStyle()}
                    />
                  </div>
                </Box>
              ) : (
                <Box
                  onClick={handleCompanyLogoClick}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 0.8,
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                >
                  <div style={{ 
                    width: '120px', 
                    height: '70px', 
                    border: '1px dashed #ccc', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '12px',
                    marginBottom: '0px',
                    marginTop: '5px'
                  }}>
                    No Logo
                  </div>
                </Box>
              )}

              {/* Username and logout button in one line below logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '10px' }}>
                <Typography component="div" sx={{ color: 'black', fontWeight: 400, fontSize: 14 }}>
                  Welcome {displayUserName}
                </Typography>

                {/* <IconButton
                  variant="contained"
                  disableElevation
                  color="error"
                  aria-label="Logout"
                  onClick={handleLogoutClick}
                  sx={{ padding: '4px' }}
                >
                  <PowerSettingsNew />
                </IconButton> */}
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            position: 'absolute',
            top: 50,
            margin: 0,
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">{'Confirm Logout'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to log out?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>No</Button>
          <Button onClick={handleConfirmLogout} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default Navbar;